import React, { useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import { BsBoundingBoxCircles, BsGeoAltFill, BsMap } from "react-icons/bs";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import OSM from "ol/source/OSM";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import CircleGeom from "ol/geom/Circle";
import { fromLonLat } from "ol/proj";
import { Circle as CircleStyle, Fill, Icon, Stroke, Style } from "ol/style";
import "ol/ol.css";
import "../../Styles/CoordenadasMapaModal.css";

const DEFAULT_CENTER = fromLonLat([-77.042341, -12.046861]);

function parseCoordinateValue(value) {
  if (!value) {
    return null;
  }

  const matches = String(value).match(/-?\d+(?:\.\d+)?/g);
  if (!matches || matches.length < 2) {
    return null;
  }

  const lat = Number.parseFloat(matches[0]);
  const lon = Number.parseFloat(matches[1]);

  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null;
  }

  return { lat, lon };
}

function createPointStyle(imageUrl) {
  const baseStyle = new Style({
    image: new CircleStyle({
      radius: 7,
      fill: new Fill({ color: "#ffffff" }),
      stroke: new Stroke({ color: "#0d6efd", width: 2 }),
    }),
  });

  if (!imageUrl) {
    return baseStyle;
  }

  return [
    baseStyle,
    new Style({
      image: new Icon({
        src: imageUrl,
        scale: 0.075,
        anchor: [0.5, 0.5],
        anchorXUnits: "fraction",
        anchorYUnits: "fraction",
      }),
    }),
  ];
}

function createCircleStyle() {
  return new Style({
    fill: new Fill({ color: "rgba(13, 110, 253, 0.12)" }),
    stroke: new Stroke({ color: "rgba(13, 110, 253, 0.52)", width: 2 }),
  });
}

export default function CoordenadasMapaModal({ show, onHide, coordenadas, routeName }) {
  const mapElementRef = useRef(null);
  const mapRef = useRef(null);
  const sourceRef = useRef(null);

  const validCoordenadas = useMemo(() => {
    return (coordenadas || [])
      .map((item) => {
        const parsed = parseCoordinateValue(item.coordenadas);
        if (!parsed) {
          return null;
        }

        return {
          ...item,
          lat: parsed.lat,
          lon: parsed.lon,
          radius: Number.parseFloat(item.radio),
        };
      })
      .filter(Boolean);
  }, [coordenadas]);

  useEffect(() => {
    if (!show || !mapElementRef.current || mapRef.current) {
      return undefined;
    }

    const source = new VectorSource();
    const map = new Map({
      target: mapElementRef.current,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
        new VectorLayer({
          source,
        }),
      ],
      view: new View({
        center: DEFAULT_CENTER,
        zoom: 12,
      }),
    });

    mapRef.current = map;
    sourceRef.current = source;

    return () => {
      map.setTarget(undefined);
      mapRef.current = null;
      sourceRef.current = null;
    };
  }, [show]);

  useEffect(() => {
    if (!show || !mapRef.current || !sourceRef.current) {
      return;
    }

    setTimeout(() => {
      mapRef.current?.updateSize();
    }, 0);
  }, [show]);

  useEffect(() => {
    if (!mapRef.current || !sourceRef.current) {
      return;
    }

    const source = sourceRef.current;
    source.clear();

    validCoordenadas.forEach((item) => {
      const center = fromLonLat([item.lon, item.lat]);
      const circleStyle = createCircleStyle();
      const pointStyle = createPointStyle(item.sonidosGeocercaModel?.urlImagen);

      if (Number.isFinite(item.radius) && item.radius > 0) {
        const circleFeature = new Feature({
          geometry: new CircleGeom(center, item.radius),
        });
        circleFeature.setStyle(circleStyle);
        source.addFeature(circleFeature);
      }

      const pointFeature = new Feature({
        geometry: new Point(center),
      });
      pointFeature.setStyle(pointStyle);
      source.addFeature(pointFeature);
    });

    if (source.getFeatures().length > 0) {
      mapRef.current.getView().fit(source.getExtent(), {
        padding: [56, 56, 56, 56],
        maxZoom: 16,
        duration: 300,
      });
      return;
    }

    mapRef.current.getView().animate({
      center: DEFAULT_CENTER,
      zoom: 12,
      duration: 300,
    });
  }, [validCoordenadas]);

  return (
    <Modal show={show} onHide={onHide} centered size="xl" dialogClassName="coordenadas-overview-shell">
      <Modal.Header closeButton>
        <Modal.Title>Geocercas de la ruta</Modal.Title>
      </Modal.Header>
      <Modal.Body className="coordenadas-overview-body">
        <div className="coordenadas-overview-copy">
          <span className="coordenadas-overview-kicker">
            <BsMap /> Vista general
          </span>
          <h4 className="coordenadas-overview-title">{routeName ? routeName : "Ruta actual"}</h4>
          <p className="coordenadas-overview-text">
            Se muestran todas las geocercas disponibles de la ruta, centradas automaticamente, con su radio y una mini
            referencia visual de la senal cuando existe imagen.
          </p>
        </div>

        <div ref={mapElementRef} className="coordenadas-overview-map" />

        <div className="coordenadas-overview-summary">
          <span className="coordenadas-overview-pill">
            <BsBoundingBoxCircles /> {validCoordenadas.length} geocercas visibles
          </span>
          <span className="coordenadas-overview-pill">
            <BsGeoAltFill /> Ajuste automatico al conjunto de coordenadas
          </span>
        </div>
      </Modal.Body>
    </Modal>
  );
}

CoordenadasMapaModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  coordenadas: PropTypes.arrayOf(PropTypes.object),
  routeName: PropTypes.string,
};

CoordenadasMapaModal.defaultProps = {
  coordenadas: [],
  routeName: "",
};
